// Shared, revision-keyed thumbnail loading for project lists.
/** Base retry delay in ms; tests shorten it. */
export let retryDelay = 1500;
export function setThumbnailRetryDelay(ms: number) {
  retryDelay = ms;
}
const imageQueue: Array<() => void> = [];
let activeImages = 0;
export function queuedImage<T>(work: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (imageQueue.length >= 100) {
      reject(new Error("Thumbnail queue busy"));
      return;
    }
    const start = () => {
      activeImages++;
      void work()
        .then(resolve, reject)
        .finally(() => {
          activeImages--;
          imageQueue.shift()?.();
        });
    };
    if (activeImages < 3) start();
    else imageQueue.push(start);
  });
}

/**
 * Revision-keyed thumbnail cache shared by every list on the page. A project's
 * image is fetched once per revision; a new revision (an update) gets a new key
 * and refreshes. Least recently used entries release their object URLs.
 */
export const thumbnailCache = new Map<string, string>();
const thumbnailRequests = new Map<string, Promise<string>>();
const maxCachedThumbnails = 300;
function rememberThumbnail(key: string, url: string) {
  thumbnailCache.delete(key);
  thumbnailCache.set(key, url);
  while (thumbnailCache.size > maxCachedThumbnails) {
    const [oldest, oldUrl] = thumbnailCache.entries().next().value!;
    thumbnailCache.delete(oldest);
    URL.revokeObjectURL(oldUrl);
  }
}
const retryable = (error: unknown) =>
  (error instanceof Error && /queue busy/i.test(error.message)) ||
  [429, 502, 503, 504].includes(
    (error as { status?: number } | undefined)?.status ?? 0,
  );
const wait = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(signal.reason);
      },
      { once: true },
    );
  });
/** Loads one thumbnail, retrying briefly when the server or queue is busy. */
export function loadThumbnail(
  key: string,
  fetchBlob: () => Promise<Blob>,
  signal: AbortSignal,
): Promise<string> {
  const cached = thumbnailCache.get(key);
  if (cached) {
    rememberThumbnail(key, cached);
    return Promise.resolve(cached);
  }
  let pending = thumbnailRequests.get(key);
  if (!pending) {
    // Shared by every list showing this project; it is not tied to one row.
    const shared = new AbortController();
    pending = (async () => {
      for (let attempt = 0; ; attempt++) {
        try {
          const blob = await queuedImage(fetchBlob);
          if (blob.type !== "image/png" || blob.size > 1024 * 1024)
            throw new Error("Invalid thumbnail");
          const url = URL.createObjectURL(blob);
          rememberThumbnail(key, url);
          return url;
        } catch (error) {
          if (attempt >= 3 || !retryable(error)) throw error;
          await wait(retryDelay * 2 ** attempt, shared.signal);
        }
      }
    })().finally(() => thumbnailRequests.delete(key));
    thumbnailRequests.set(key, pending);
  }
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(signal.reason);
    signal.addEventListener("abort", () => reject(signal.reason), {
      once: true,
    });
    pending!.then(resolve, reject);
  });
}

